import React, { useCallback, useEffect, useRef, useState } from 'react'

interface ResizablePanelProps {
  direction: 'horizontal' | 'vertical'
  initialSize: number
  minSize?: number
  maxSize?: number
  storageKey?: string
  reverse?: boolean
  children: [React.ReactNode, React.ReactNode]
}

export function ResizablePanel({
  direction,
  initialSize,
  minSize = 100,
  maxSize = 800,
  storageKey,
  reverse = false,
  children,
}: ResizablePanelProps) {
  const stored = storageKey ? Number(localStorage.getItem(storageKey)) || initialSize : initialSize
  const [size, setSize] = useState(stored)
  const isDragging = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const isH = direction === 'horizontal'

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    isDragging.current = true
    document.body.style.cursor = isH ? 'col-resize' : 'row-resize'
    document.body.style.userSelect = 'none'
  }, [isH])

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isDragging.current || !containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()

      let raw: number
      if (reverse) {
        // In reverse mode, size is measured from the end
        raw = isH ? rect.right - e.clientX : rect.bottom - e.clientY
      } else {
        raw = isH ? e.clientX - rect.left : e.clientY - rect.top
      }

      const clamped = Math.min(Math.max(raw, minSize), maxSize)
      setSize(clamped)
      if (storageKey) localStorage.setItem(storageKey, String(clamped))
    }
    const onUp = () => {
      isDragging.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp) }
  }, [isH, minSize, maxSize, storageKey, reverse])

  // reverse: second child has fixed size (bottom/right), first child fills
  // normal: first child has fixed size (top/left), second child fills
  const fixedStyle: React.CSSProperties = isH
    ? { width: size, height: '100%', flexShrink: 0, overflow: 'hidden' }
    : { height: size, width: '100%', flexShrink: 0, overflow: 'hidden' }

  const fillStyle: React.CSSProperties = { flex: 1, height: '100%', overflow: 'hidden', minWidth: 0, minHeight: 0 }

  return (
    <div
      ref={containerRef}
      style={{ display: 'flex', flexDirection: isH ? 'row' : 'column', flex: 1, height: '100%', overflow: 'hidden', minWidth: 0, minHeight: 0 }}
    >
      {reverse ? (
        <>
          <div style={fillStyle}>{children[0]}</div>
          <div
            onMouseDown={handleMouseDown}
            style={{
              [isH ? 'width' : 'height']: '1px',
              [isH ? 'height' : 'width']: '100%',
              flexShrink: 0,
              cursor: isH ? 'col-resize' : 'row-resize',
              background: 'var(--border)',
              transition: 'background 0.15s',
              zIndex: 10,
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--accent)' }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--border)' }}
          />
          <div style={fixedStyle}>{children[1]}</div>
        </>
      ) : (
        <>
          <div style={fixedStyle}>{children[0]}</div>
          <div
            onMouseDown={handleMouseDown}
            style={{
              [isH ? 'width' : 'height']: '1px',
              [isH ? 'height' : 'width']: '100%',
              flexShrink: 0,
              cursor: isH ? 'col-resize' : 'row-resize',
              background: 'var(--border)',
              transition: 'background 0.15s',
              zIndex: 10,
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--accent)' }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--border)' }}
          />
          <div style={fillStyle}>{children[1]}</div>
        </>
      )}
    </div>
  )
}
